import React from 'react';
import { Box, Tabs, Tab, useTheme, useMediaQuery } from '@mui/material';
import { Task, TimelineEvent, Document } from '../../types';
import { CaseDetailsContent } from './CaseDetailsContent';
import { Timeline } from './Timeline';
import { TaskFormsTab } from './TaskForms/TaskFormsTab';
import { DocumentsTab } from './Documents/DocumentsTab';
import { CalendarTab } from './Calendar/CalendarTab';
import { ContactsTab } from './Contacts/ContactsTab';

interface CaseDetailsTabsProps {
  tasks: Task[];
  timeline: TimelineEvent[];
  documents: Document[];
  stageId: string;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => void;
  onTaskCreate: (task: Partial<Task>) => void;
  activeTab: number;
  onTabChange: (newTab: number) => void;
  onFormOpen?: (task: Task) => void;
  selectedTaskId: string | null;
  onTaskSelect: (taskId: string | null) => void;
  onAddTimelineNote?: (note: Omit<TimelineEvent, 'id'>) => void;
  onDocumentUpload?: (document: Omit<Document, 'id' | 'uploadDate'>, taskId?: string) => void;
  onFormView?: (taskId: string) => void;
  onDocumentView?: (documentId: string) => void;
  caseName: string;
}

export const CaseDetailsTabs: React.FC<CaseDetailsTabsProps> = ({
  tasks,
  timeline,
  documents,
  stageId,
  onTaskUpdate,
  onTaskCreate,
  activeTab,
  onTabChange,
  onFormOpen,
  selectedTaskId,
  onTaskSelect,
  onAddTimelineNote,
  onDocumentUpload,
  onFormView,
  onDocumentView,
  caseName,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    onTabChange(newValue);
    if (newValue !== 2) {
      onTaskSelect(null);
    }
  };

  const handleTaskComplete = (taskId: string) => {
    onTaskUpdate(taskId, {
      status: 'completed',
      metadata: {
        completedAt: new Date().toISOString(),
      },
    });
  };

  return (
    <Box>
      <Box 
        sx={{ 
          mb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: { xs: 48, sm: 'auto' },
            '& .MuiTabs-flexContainer': {
              gap: { xs: 1, sm: 2 },
            },
            '& .MuiTab-root': {
              minWidth: { xs: 'auto', sm: 120 },
              minHeight: { xs: 48, sm: 'auto' },
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
            },
          }}
        >
          <Tab label="Tasks" />
          <Tab label="Timeline" />
          <Tab label="Forms" />
          <Tab label="Documents" />
          <Tab label="Calendar" />
          <Tab label="Contacts" />
        </Tabs>
      </Box>

      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        {activeTab === 0 && (
          <CaseDetailsContent
            tasks={tasks}
            stageId={stageId}
            onTaskUpdate={onTaskUpdate}
            onTaskCreate={onTaskCreate}
            onFormOpen={onFormOpen}
          />
        )}

        {activeTab === 1 && (
          <Timeline 
            events={timeline} 
            onAddNote={onAddTimelineNote}
            onFormView={onFormView}
            onDocumentView={onDocumentView}
          />
        )}

        {activeTab === 2 && (
          <TaskFormsTab
            tasks={tasks}
            onTaskUpdate={onTaskUpdate}
            selectedTaskId={selectedTaskId}
            onTaskSelect={onTaskSelect}
            onDocumentUpload={onDocumentUpload}
          />
        )}

        {activeTab === 3 && (
          <DocumentsTab
            documents={documents}
            tasks={tasks}
            onDocumentUpload={onDocumentUpload}
            onTaskComplete={handleTaskComplete}
          />
        )}

        {activeTab === 4 && (
          <CalendarTab
            tasks={tasks}
            keyDates={timeline.filter(event => event.type === 'court-date' || event.type === 'custom')}
            onAddEvent={onAddTimelineNote}
            caseName={caseName}
          />
        )}

        {activeTab === 5 && (
          <ContactsTab />
        )}
      </Box>
    </Box>
  );
};